import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Wallet, Camera, Plus, Check, X, Receipt, Utensils, Wrench, Warehouse,
  Package, Loader2, Image as ImageIcon, ArrowUp, Trash2, RotateCcw,
} from "lucide-react";

const CATEGORIES = [
  { id: "yemek",   label: "Yemek / Kumanya", icon: Utensils,  color: "bg-orange-100 text-orange-800 border-orange-300" },
  { id: "sanayi",  label: "Sanayi / Yedek Parça", icon: Wrench, color: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "garaj",   label: "Garaj Gideri", icon: Warehouse, color: "bg-purple-100 text-purple-800 border-purple-300" },
  { id: "diger",   label: "Diğer", icon: Package, color: "bg-slate-100 text-slate-700 border-slate-300" },
];
const CAT_META = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

const fmtTl = (n) => `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

export default function PettyCashDrawer() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("pending"); // pending | paid | new
  const [summary, setSummary] = useState({ pending_count: 0, pending_total: 0, paid_count: 0, paid_total: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/expenses/summary");
      setSummary(data);
    } catch { /* silent */ }
  }, []);

  const refreshList = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses", { params: { status } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshSummary();
    const t = setInterval(refreshSummary, 30000);
    return () => clearInterval(t);
  }, [user, refreshSummary]);

  useEffect(() => {
    if (!open) return;
    if (tab === "pending") refreshList("pending");
    else if (tab === "paid") refreshList("paid");
  }, [open, tab, refreshList]);

  const setBusy = (id, on) => setBusyIds((prev) => {
    const n = new Set(prev);
    if (on) n.add(id); else n.delete(id);
    return n;
  });

  const markPaid = async (id) => {
    if (!isAdmin) return;
    setBusy(id, true);
    // optimistic
    setItems((prev) => prev.filter((e) => e.id !== id));
    try {
      await api.post(`/expenses/${id}/mark-paid`);
      toast.success("Ödendi olarak işaretlendi");
      refreshSummary();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşaretlenemedi");
      refreshList("pending");
    } finally { setBusy(id, false); }
  };

  const reopenExpense = async (id) => {
    if (!isAdmin) return;
    setBusy(id, true);
    try {
      await api.post(`/expenses/${id}/reopen`);
      toast.success("Ödeme yeniden açıldı");
      refreshSummary();
      refreshList(tab === "paid" ? "paid" : "pending");
    } catch (e) {
      toast.error("Yeniden açılamadı");
    } finally { setBusy(id, false); }
  };

  const deleteExpense = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Bu masraf kaydı silinsin mi?")) return;
    setBusy(id, true);
    try {
      await api.delete(`/expenses/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      refreshSummary();
      toast.success("Silindi");
    } catch (e) {
      toast.error("Silinemedi");
    } finally { setBusy(id, false); }
  };

  const openReceipt = async (item) => {
    if (!item.receipt_path) return;
    setPreviewId(item.id);
    try {
      const res = await api.get(`/expenses/${item.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error("Fiş görüntülenemedi");
      setPreviewId(null);
    }
  };

  const closeReceipt = () => {
    setPreviewId(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const onSubmitted = () => {
    setTab("pending");
    refreshSummary();
    refreshList("pending");
  };

  // Listen for top-bar Kasa button
  useEffect(() => {
    const openDrawer = () => setOpen(true);
    window.addEventListener("yaztur:open-petty-cash", openDrawer);
    return () => window.removeEventListener("yaztur:open-petty-cash", openDrawer);
  }, []);

  // Render guard AFTER all hooks (React rules-of-hooks compliance)
  if (!user) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="petty-cash-drawer">
          <SheetHeader className="p-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white shrink-0">
            <SheetTitle className="flex items-center gap-2 font-heading">
              <Wallet className="w-5 h-5 text-[#1E3A8A]" /> Kasa Çekmecesi — Cepten Masraflar
            </SheetTitle>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-red-700">Hazırlanacak Nakit</div>
                <div className="mt-0.5 font-mono font-bold text-2xl text-red-800">{fmtTl(summary.pending_total)}</div>
                <div className="text-[11px] text-red-600 mt-0.5">{summary.pending_count} bekleyen</div>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Ödenen Toplam</div>
                <div className="mt-0.5 font-mono font-bold text-2xl text-emerald-800">{fmtTl(summary.paid_total)}</div>
                <div className="text-[11px] text-emerald-600 mt-0.5">{summary.paid_count} kayıt</div>
              </div>
            </div>
            <div className="flex gap-1 mt-3 border-b border-slate-200 -mb-4">
              <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="petty-tab-pending">🕒 Bekleyen ({summary.pending_count})</TabBtn>
              <TabBtn active={tab === "paid"} onClick={() => setTab("paid")} testid="petty-tab-paid">✅ Ödenenler</TabBtn>
              <TabBtn active={tab === "new"} onClick={() => setTab("new")} testid="petty-tab-new" highlight><Plus className="w-3.5 h-3.5 inline mr-1" />Yeni Masraf</TabBtn>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-4">
            {tab === "new" && (
              <NewExpenseForm onSubmitted={onSubmitted} onCancel={() => setTab("pending")} />
            )}
            {tab !== "new" && (
              <ExpenseList
                items={items}
                loading={loading}
                isAdmin={isAdmin}
                tab={tab}
                busyIds={busyIds}
                onReceipt={openReceipt}
                onMarkPaid={markPaid}
                onReopen={reopenExpense}
                onDelete={deleteExpense}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Receipt preview modal */}
      <Dialog open={!!previewId} onOpenChange={(o) => { if (!o) closeReceipt(); }}>
        <DialogContent className="max-w-3xl p-2">
          <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100 mb-2">
            <div className="text-sm font-medium text-slate-700 flex items-center gap-1"><Receipt className="w-4 h-4" /> Fiş Önizleme</div>
            <button onClick={closeReceipt} className="text-slate-500 hover:text-slate-800" data-testid="close-receipt-preview"><X className="w-4 h-4" /></button>
          </div>
          {previewUrl ? (
            <img src={previewUrl} alt="Fiş" className="w-full max-h-[70vh] object-contain rounded" data-testid="receipt-preview-img" />
          ) : (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TabBtn({ active, onClick, children, testid, highlight }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-all ${
        active
          ? highlight ? "border-emerald-500 text-emerald-700" : "border-[#1E3A8A] text-[#1E3A8A]"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >{children}</button>
  );
}

function CategoryBadge({ id }) {
  const c = CAT_META[id] || CAT_META.diger;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded border ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label.split(" / ")[0]}
    </span>
  );
}

function ExpenseList({ items, loading, isAdmin, tab, busyIds, onReceipt, onMarkPaid, onReopen, onDelete }) {
  if (loading) return <div className="text-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Yükleniyor...</div>;
  if (!items.length) return (
    <div className="text-center py-14 text-slate-400 text-sm">
      {tab === "pending" ? "🎉 Bekleyen masraf yok — kasa temiz." : "Henüz ödeme yapılmadı."}
    </div>
  );
  return (
    <div className="space-y-2">
      {items.map((e) => (
        <div key={e.id} data-testid={`expense-item-${e.id}`}
          className={`rounded-lg border p-3 bg-white hover:shadow-sm transition-all ${
            e.status === "pending" ? "border-l-4 border-l-amber-500 border-y border-r border-slate-200" : "border-slate-200 opacity-90"
          }`}>
          <div className="flex items-start gap-3">
            {e.receipt_path ? (
              <button
                onClick={() => onReceipt(e)}
                title="Fiş göster"
                data-testid={`expense-receipt-${e.id}`}
                className="shrink-0 w-14 h-14 rounded border-2 border-dashed border-blue-300 bg-blue-50 flex items-center justify-center hover:bg-blue-100 transition-colors"
              >
                <ImageIcon className="w-5 h-5 text-blue-600" />
              </button>
            ) : (
              <div className="shrink-0 w-14 h-14 rounded border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">
                <Receipt className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CategoryBadge id={e.category} />
                <span className="text-[10px] font-mono text-slate-500">{fmtDate(e.created_at)}</span>
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900 break-words">{e.description}</div>
              <div className="mt-1 text-[11px] text-slate-500">
                Harcayan: <span className="font-medium text-slate-700">{e.spender_name || "—"}</span>
                {e.paid_by_name && (
                  <> · Onaylayan: <span className="font-medium text-emerald-700">{e.paid_by_name}</span> · {fmtDate(e.paid_at)}</>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className={`font-mono font-bold text-lg ${e.status === "pending" ? "text-red-700" : "text-emerald-700"}`}>{fmtTl(e.amount_tl)}</div>
              <div className="mt-2 flex items-center justify-end gap-1">
                {e.status === "pending" && isAdmin && (
                  <Button
                    size="sm"
                    onClick={() => onMarkPaid(e.id)}
                    disabled={busyIds.has(e.id)}
                    data-testid={`expense-pay-${e.id}`}
                    className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs px-2"
                  >
                    <Check className="w-3 h-3 mr-1" /> Ödendi
                  </Button>
                )}
                {e.status === "paid" && isAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReopen(e.id)}
                    disabled={busyIds.has(e.id)}
                    data-testid={`expense-reopen-${e.id}`}
                    className="h-7 text-xs px-2"
                    title="Yeniden aç"
                  >
                    <RotateCcw className="w-3 h-3" />
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(e.id)}
                    disabled={busyIds.has(e.id)}
                    data-testid={`expense-delete-${e.id}`}
                    className="h-7 text-xs px-2 text-slate-400 hover:text-red-600"
                    title="Sil"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewExpenseForm({ onSubmitted, onCancel }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("diger");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Dosya çok büyük (max 15MB)"); return; }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const submit = async () => {
    const desc = description.trim();
    const amt = parseFloat(amount);
    if (!desc) return toast.error("Açıklama gerekli");
    if (isNaN(amt) || amt <= 0) return toast.error("Geçerli bir tutar girin");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("description", desc);
    fd.append("amount_tl", String(amt));
    fd.append("category", category);
    if (file) fd.append("receipt", file);
    try {
      await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Masraf eklendi ✓");
      setDescription(""); setAmount(""); setCategory("diger"); clearFile();
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kayıt hatası");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-slate-600">Masraf Açıklaması</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Örn: Bakkal — su/çay alımı"
          className="mt-1 text-sm"
          data-testid="expense-desc-input"
          maxLength={400}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-slate-600">Tutar (TL)</Label>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="750"
            className="mt-1 font-mono text-lg h-11"
            data-testid="expense-amount-input"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-slate-600">Kategori</Label>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  data-testid={`expense-cat-${c.id}`}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded border text-[11px] transition-all ${active ? c.color + " ring-2 ring-offset-1 ring-slate-400" : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"}`}
                >
                  <Icon className="w-3 h-3" /> {c.label.split(" / ")[0]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs font-mono uppercase tracking-widest text-slate-600">📸 Fiş / Makbuz Fotoğrafı</Label>
        {previewUrl ? (
          <div className="mt-2 relative">
            <img src={previewUrl} alt="Fiş önizleme" className="w-full max-h-48 object-contain rounded border border-slate-200 bg-slate-50" data-testid="expense-receipt-preview" />
            <button onClick={clearFile} className="absolute top-2 right-2 p-1 rounded-full bg-white/90 shadow border border-slate-200 text-slate-600 hover:text-red-600" data-testid="expense-receipt-clear"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              onClick={() => cameraRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1 py-4 rounded border-2 border-dashed border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700"
              data-testid="expense-take-photo-btn"
            >
              <Camera className="w-5 h-5" />
              <span className="text-[11px] font-medium">Kamera Aç</span>
            </button>
            <button
              onClick={() => galleryRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1 py-4 rounded border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600"
              data-testid="expense-pick-photo-btn"
            >
              <ImageIcon className="w-5 h-5" />
              <span className="text-[11px] font-medium">Galeriden Seç</span>
            </button>
          </div>
        )}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFilePick} className="hidden" />
        <input ref={galleryRef} type="file" accept="image/*" onChange={onFilePick} className="hidden" />
      </div>
      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <Button onClick={submit} disabled={submitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="expense-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUp className="w-4 h-4 mr-2" />}
          {submitting ? "Kaydediliyor..." : "Masrafı Kaydet"}
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="expense-cancel-btn">İptal</Button>
      </div>
    </div>
  );
}
